import React, { useState, useEffect } from 'react';
import { BarChart2, Clock, Filter, X, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import api, { StrategyHistorySummary, StrategyHistoryDetail } from '../services/api';

const MARKET_LABELS: Record<string, string> = {
  jse: 'JSE',
  us: 'US',
  london: 'LONDON',
};

const MARKET_COLORS: Record<string, string> = {
  jse: 'bg-amber-500/20 text-amber-400',
  us: 'bg-blue-500/20 text-blue-400',
  london: 'bg-purple-500/20 text-purple-400',
};

const DIRECTION_COLORS: Record<string, string> = {
  BUY: 'text-emerald-400',
  SELL: 'text-red-400',
  HOLD: 'text-slate-400',
};

const StrategyHistory: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<StrategyHistorySummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterMarket, setFilterMarket] = useState<string>('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<StrategyHistoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadHistory();
  }, [filterMarket]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getStrategyHistory(filterMarket || undefined, 100);
      setHistory(data);
    } catch (err) {
      console.error('Failed to load strategy history:', err);
      setError('Failed to load strategy history');
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetails(null);
      return;
    }

    try {
      setDetailLoading(true);
      const details = await api.getStrategyHistoryById(id);
      setExpandedDetails(details);
      setExpandedId(id);
    } catch (err) {
      console.error('Failed to load strategy run details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <BarChart2 className="text-teal-400" size={32} />
          <h1 className="text-3xl font-bold">Strategy Signal History</h1>
        </div>
        <p className="text-slate-400">
          Review past screener runs to refine your strategy parameters
        </p>
      </div>

      {/* Filter Bar */}
      <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-slate-400">
            <Filter size={18} />
            <span className="text-sm font-medium">Filter by Market:</span>
          </div>
          <div className="flex items-center gap-2">
            {['', 'jse', 'us', 'london'].map((m) => (
              <button
                key={m}
                onClick={() => setFilterMarket(m)}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors border ${
                  filterMarket === m
                    ? 'bg-teal-500/20 border-teal-500/50 text-teal-400'
                    : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {m === '' ? 'All' : m.toUpperCase()}
              </button>
            ))}
          </div>
          {filterMarket && (
            <button
              onClick={() => setFilterMarket('')}
              className="px-2 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-slate-400"
              title="Clear filter"
            >
              <X size={16} />
            </button>
          )}
          {history.length > 0 && (
            <div className="ml-auto text-sm text-slate-400">
              {history.length} {history.length === 1 ? 'run' : 'runs'}
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-400/10 border border-red-400/20 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && history.length === 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-12 text-center">
          <BarChart2 size={64} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No strategy runs yet</h3>
          <p className="text-slate-400 mb-6">
            {filterMarket
              ? `No runs found for ${filterMarket.toUpperCase()}`
              : "Use the Screener's Match Strategy button to generate signals"}
          </p>
          {filterMarket && (
            <button
              onClick={() => setFilterMarket('')}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 rounded-lg transition-colors"
            >
              Clear Filter
            </button>
          )}
        </div>
      )}

      {/* History List */}
      {history.length > 0 && (
        <div className="space-y-4">
          {history.map((item) => (
            <div
              key={item.id}
              className="bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden hover:border-slate-600 transition-colors"
            >
              {/* Row Header */}
              <div
                onClick={() => loadDetails(item.id)}
                className="p-4 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      {item.market && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${MARKET_COLORS[item.market] ?? 'bg-slate-700 text-slate-300'}`}>
                          {MARKET_LABELS[item.market] ?? item.market.toUpperCase()}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-slate-200">
                        {item.signal_count} {item.signal_count === 1 ? 'signal' : 'signals'}
                      </span>
                      <span className="text-xs text-slate-500 truncate max-w-xs" title={item.strategy_names.join(', ')}>
                        {item.strategy_names.join(', ').length > 50
                          ? `${item.strategy_names.join(', ').slice(0, 50)}…`
                          : item.strategy_names.join(', ') || 'No strategies'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock size={12} />
                      <span>{formatDate(item.computed_at)}</span>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    {expandedId === item.id ? (
                      <ChevronUp size={20} className="text-slate-400" />
                    ) : (
                      <ChevronDown size={20} className="text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedId === item.id && (
                <div className="border-t border-slate-700">
                  {detailLoading && !expandedDetails ? (
                    <div className="p-6 flex justify-center">
                      <LoadingSpinner />
                    </div>
                  ) : expandedDetails && expandedDetails.id === item.id ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-700/50 text-left">
                            <th className="px-4 py-2 text-xs font-semibold text-slate-400">Symbol</th>
                            <th className="px-4 py-2 text-xs font-semibold text-slate-400">Direction</th>
                            <th className="px-4 py-2 text-xs font-semibold text-slate-400 text-right">Match Score</th>
                            <th className="px-4 py-2 text-xs font-semibold text-slate-400">Matched Strategy</th>
                            <th className="px-4 py-2 text-xs font-semibold text-slate-400 text-right">Confidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(expandedDetails.signals as any[])
                            .slice()
                            .sort((a: any, b: any) => (b.match_score ?? 0) - (a.match_score ?? 0))
                            .map((sig: any, i: number) => (
                              <tr key={i} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                                <td className="px-4 py-2">
                                  <Link
                                    to={`/asset/${sig.symbol}`}
                                    className="font-mono text-teal-400 hover:text-teal-300 transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {sig.symbol}
                                  </Link>
                                </td>
                                <td className="px-4 py-2">
                                  <span className={`font-semibold text-xs ${DIRECTION_COLORS[sig.signal?.toUpperCase()] ?? DIRECTION_COLORS[sig.direction?.toUpperCase()] ?? 'text-slate-400'}`}>
                                    {sig.signal ?? sig.direction ?? '—'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-slate-300">
                                  {sig.match_score != null ? `${sig.match_score.toFixed(0)}%` : '—'}
                                </td>
                                <td className="px-4 py-2 text-slate-400 text-xs">
                                  {sig.matched_strategy ?? '—'}
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-slate-300">
                                  {sig.confidence != null ? `${(sig.confidence * 100).toFixed(0)}%` : '—'}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StrategyHistory;
